import { useCallback, useMemo, useState } from 'react'
import type { AppendixCodeItemOption, AppendixColumnDataType } from '../types/codeIndex'
import {
  AppendixColumnDraft,
  AppendixColumnError,
  AppendixRowDraft,
  applyColumnTypeChange,
  createColumnDraft,
  createRowDraft,
  describeInvalidValues,
  findCellsClearedByTypeChange,
  findCodeItemOption,
  formatCount,
  getColumnTypeNoun,
  moveItem,
  removeColumn,
  validateCell,
  validateColumns,
} from '../utils/appendixTable'

type TableDraft = {
  columns: AppendixColumnDraft[]
  rows: AppendixRowDraft[]
}

export type AppendixUndoToast = {
  id: number
  title: string
  message: string
}

export type PendingColumnTypeChange = {
  columnKey: string
  columnLabel: string
  fromType: AppendixColumnDataType
  fromCodeItemCode: string
  toType: AppendixColumnDataType
  toCodeItemCode: string
  clearedValues: string[]
}

type UndoState = {
  snapshot: TableDraft
  toast: AppendixUndoToast
}

const EMPTY_DRAFT: TableDraft = { columns: [], rows: [] }

export function getCellErrorKey(rowKey: string, columnKey: string): string {
  return `${rowKey}::${columnKey}`
}

function columnDisplayLabel(column: AppendixColumnDraft, columns: AppendixColumnDraft[]): string {
  return column.label.trim() || `Column ${columns.indexOf(column) + 1}`
}

function withoutColumnErrors(errors: Record<string, string>, columnKey: string): Record<string, string> {
  const suffix = `::${columnKey}`
  return Object.fromEntries(Object.entries(errors).filter(([key]) => !key.endsWith(suffix)))
}

function withoutRowErrors(errors: Record<string, string>, rowKey: string): Record<string, string> {
  const prefix = `${rowKey}::`
  return Object.fromEntries(Object.entries(errors).filter(([key]) => !key.startsWith(prefix)))
}

function withError(errors: Record<string, string>, key: string, error: string | null): Record<string, string> {
  if (error) return errors[key] === error ? errors : { ...errors, [key]: error }
  return withoutKey(errors, key)
}

function withoutKey<T>(errors: Record<string, T>, key: string): Record<string, T> {
  if (!(key in errors)) return errors
  const { [key]: _removed, ...rest } = errors
  return rest
}

export function useAppendixTableDraft(codeItems: AppendixCodeItemOption[]) {
  const [draft, setDraft] = useState<TableDraft>(EMPTY_DRAFT)
  const [cellErrors, setCellErrors] = useState<Record<string, string>>({})
  const [columnErrors, setColumnErrors] = useState<Record<string, AppendixColumnError>>({})
  const [pendingTypeChange, setPendingTypeChange] = useState<PendingColumnTypeChange | null>(null)
  const [undoState, setUndoState] = useState<UndoState | null>(null)

  const { columns, rows } = draft

  // Replaces the whole draft, e.g. with an empty table or one loaded from the server.
  const reset = useCallback((next: TableDraft = EMPTY_DRAFT) => {
    setDraft(next)
    setCellErrors({})
    setColumnErrors({})
    setPendingTypeChange(null)
    setUndoState(null)
  }, [])

  // Undo only restores the draft as it was right before the action, so any later edit retires it.
  const updateDraft = (next: TableDraft) => {
    setDraft(next)
    setUndoState(null)
  }

  const showUndo = (snapshot: TableDraft, title: string, message: string) => {
    setUndoState(current => ({
      snapshot,
      toast: { id: (current?.toast.id ?? 0) + 1, title, message },
    }))
  }

  const validateCellValue = (column: AppendixColumnDraft, value: string) =>
    validateCell(column, value, findCodeItemOption(codeItems, column.codeItemCode))

  const addColumn = (): string => {
    const column = createColumnDraft()
    updateDraft({
      columns: [...columns, column],
      rows: rows.map(row => ({ ...row, cells: { ...row.cells, [column.key]: '' } })),
    })
    return column.key
  }

  const updateColumn = (columnKey: string, patch: Partial<Pick<AppendixColumnDraft, 'label' | 'unit'>>) => {
    updateDraft({
      columns: columns.map(column => column.key === columnKey ? { ...column, ...patch } : column),
      rows,
    })
    setColumnErrors(current => withoutKey(current, columnKey))
  }

  const applyTypeChange = (columnKey: string, toType: AppendixColumnDataType, toCodeItemCode: string) => {
    const nextCodeItem = findCodeItemOption(codeItems, toCodeItemCode)
    const result = applyColumnTypeChange(columns, rows, columnKey, toType, nextCodeItem)
    setDraft({ columns: result.columns, rows: result.rows })
    setCellErrors(current => withoutColumnErrors(current, columnKey))
    setColumnErrors(current => withoutKey(current, columnKey))
    return result
  }

  // Changes that would clear cells ask first; anything else applies right away.
  const requestColumnTypeChange = (columnKey: string, toType: AppendixColumnDataType, toCodeItemCode = '') => {
    const column = columns.find(item => item.key === columnKey)
    if (!column) return

    const nextCodeItemCode = toType === 'CodeItem' ? toCodeItemCode : ''
    if (column.dataType === toType && column.codeItemCode === nextCodeItemCode) return

    const clearedValues = findCellsClearedByTypeChange(
      rows,
      columnKey,
      toType,
      findCodeItemOption(codeItems, nextCodeItemCode)
    )

    if (clearedValues.length > 0) {
      setPendingTypeChange({
        columnKey,
        columnLabel: columnDisplayLabel(column, columns),
        fromType: column.dataType,
        fromCodeItemCode: column.codeItemCode,
        toType,
        toCodeItemCode: nextCodeItemCode,
        clearedValues,
      })
      return
    }

    applyTypeChange(columnKey, toType, nextCodeItemCode)
    setUndoState(null)
  }

  const confirmTypeChange = () => {
    if (!pendingTypeChange) return

    const snapshot = draft
    const { columnKey, columnLabel, toType, toCodeItemCode } = pendingTypeChange
    const { clearedCount } = applyTypeChange(columnKey, toType, toCodeItemCode)
    setPendingTypeChange(null)

    const verb = clearedCount === 1 ? 'cell held a value that wasn\'t' : 'cells held values that weren\'t'
    const outcome = clearedCount === 1 ? 'It has been cleared.' : 'They have been cleared.'
    showUndo(
      snapshot,
      toType === 'CodeItem' && pendingTypeChange.fromType === 'CodeItem'
        ? `${columnLabel} now references code item ${toCodeItemCode}`
        : `${columnLabel} is now a ${getColumnTypeNoun(toType)} column`,
      `${clearedCount} ${verb} ${describeInvalidValues(toType, toCodeItemCode, clearedCount)}. ${outcome}`
    )
  }

  const cancelTypeChange = () => setPendingTypeChange(null)

  const deleteColumn = (columnKey: string) => {
    const column = columns.find(item => item.key === columnKey)
    if (!column) return

    const snapshot = draft
    const label = columnDisplayLabel(column, columns)
    const result = removeColumn(columns, rows, columnKey)
    setDraft({ columns: result.columns, rows: result.rows })
    setCellErrors(current => withoutColumnErrors(current, columnKey))
    setColumnErrors(current => withoutKey(current, columnKey))

    const deleted = `${formatCount(result.removedCount, 'value')} ${result.removedCount === 1 ? 'was' : 'were'} deleted`
    const reference = column.dataType === 'CodeItem' && column.codeItemCode
      ? `, and the reference to code item ${column.codeItemCode} is gone`
      : ''
    showUndo(snapshot, `${label} column removed`, `${deleted}${reference}.`)
  }

  const moveColumn = (fromIndex: number, toIndex: number) => {
    const nextColumns = moveItem(columns, fromIndex, toIndex)
    if (nextColumns !== columns) updateDraft({ columns: nextColumns, rows })
  }

  const addRow = () => {
    if (columns.length === 0) return
    updateDraft({ columns, rows: [...rows, createRowDraft(columns)] })
  }

  const deleteRow = (rowKey: string) => {
    updateDraft({ columns, rows: rows.filter(row => row.key !== rowKey) })
    setCellErrors(current => withoutRowErrors(current, rowKey))
  }

  const setCellError = (rowKey: string, column: AppendixColumnDraft, value: string) => {
    const key = getCellErrorKey(rowKey, column.key)
    setCellErrors(current => withError(current, key, validateCellValue(column, value)))
  }

  // Cells validate on blur; a cell already flagged revalidates while typing so the error clears once fixed.
  const updateCell = (rowKey: string, columnKey: string, value: string, validateNow = false) => {
    updateDraft({
      columns,
      rows: rows.map(row => row.key === rowKey ? { ...row, cells: { ...row.cells, [columnKey]: value } } : row),
    })

    const column = columns.find(item => item.key === columnKey)
    if (column && (validateNow || cellErrors[getCellErrorKey(rowKey, columnKey)])) {
      setCellError(rowKey, column, value)
    }
  }

  const validateCellAt = (rowKey: string, columnKey: string) => {
    const column = columns.find(item => item.key === columnKey)
    const row = rows.find(item => item.key === rowKey)
    if (!column || !row) return
    setCellError(rowKey, column, row.cells[columnKey] ?? '')
  }

  const validateAll = (): { columnsValid: boolean; cellsValid: boolean } => {
    const nextColumnErrors = validateColumns(columns)
    const nextCellErrors: Record<string, string> = {}
    rows.forEach(row => {
      columns.forEach(column => {
        const error = validateCellValue(column, row.cells[column.key] ?? '')
        if (error) nextCellErrors[getCellErrorKey(row.key, column.key)] = error
      })
    })

    setColumnErrors(nextColumnErrors)
    setCellErrors(nextCellErrors)
    return {
      columnsValid: Object.keys(nextColumnErrors).length === 0,
      cellsValid: Object.keys(nextCellErrors).length === 0,
    }
  }

  const undo = () => {
    if (!undoState) return
    setDraft(undoState.snapshot)
    setCellErrors({})
    setColumnErrors({})
    setUndoState(null)
  }

  const dismissUndo = useCallback(() => setUndoState(null), [])

  const firstCellError = useMemo(() => {
    for (const row of rows) {
      for (const column of columns) {
        const error = cellErrors[getCellErrorKey(row.key, column.key)]
        if (error) return error
      }
    }
    return null
  }, [cellErrors, columns, rows])

  const firstColumnError = useMemo(
    () => columns.map(column => columnErrors[column.key]?.message).find(Boolean) || null,
    [columnErrors, columns]
  )

  return {
    columns,
    rows,
    cellErrors,
    columnErrors,
    firstCellError,
    firstColumnError,
    pendingTypeChange,
    undoToast: undoState?.toast ?? null,
    reset,
    addColumn,
    updateColumn,
    requestColumnTypeChange,
    confirmTypeChange,
    cancelTypeChange,
    deleteColumn,
    moveColumn,
    addRow,
    deleteRow,
    updateCell,
    validateCellAt,
    validateAll,
    undo,
    dismissUndo,
  }
}

export default useAppendixTableDraft
